import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, json } from '../lib/api';
import type { User } from '../types';

export interface Branding {
  appName: string;
  appLogo: string | null;
  homeIcon: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

interface AuthValue {
  user: User | null;
  loading: boolean;
  branding: Branding | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  refreshBranding: () => Promise<Branding | null>;
  setLiveTheme: (colors: { primaryColor?: string; secondaryColor?: string; accentColor?: string }) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

function syncPwaBranding(branding: Branding | null) {
  if (typeof document === 'undefined') return;
  const icon = branding?.homeIcon || branding?.appLogo;
  if (icon) {
    let appleTouch = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (!appleTouch) {
      appleTouch = document.createElement('link');
      appleTouch.rel = 'apple-touch-icon';
      document.head.appendChild(appleTouch);
    }
    appleTouch.href = icon;

    let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = icon;
  }

  if (branding?.appName) {
    document.title = branding.appName;
  }

  // NOTE: Do NOT dynamically replace the <link rel="manifest"> tag here.
  // Chromium browsers reject blob: manifest URLs for PWA installability.
  // The static /manifest.webmanifest file handles PWA installation correctly.
}

function applyTheme(user: User | null, branding: Branding | null) {
  const root = document.documentElement;
  // Priority:
  //   1. If no user (login screen) → always use global branding colors
  //   2. If user has useCustomColors=true → use their own manual colors
  //   3. Otherwise → inherit global branding colors as default
  let primary: string;
  let secondary: string;
  let accent: string;
  if (user && user.useCustomColors) {
    primary = user.primaryColor || '#153B50';
    secondary = user.secondaryColor || '#F3F7F5';
    accent = user.accentColor || '#D8FF4F';
  } else {
    primary = branding?.primaryColor || '#153B50';
    secondary = branding?.secondaryColor || '#F3F7F5';
    accent = branding?.accentColor || '#D8FF4F';
  }
  root.style.setProperty('--primary', primary);
  root.style.setProperty('--secondary', secondary);
  root.style.setProperty('--accent', accent);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', primary);
  syncPwaBranding(branding);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState<Branding | null>(null);

  const setLiveTheme = useCallback((colors: { primaryColor?: string; secondaryColor?: string; accentColor?: string }) => {
    const root = document.documentElement;
    if (colors.primaryColor) {
      root.style.setProperty('--primary', colors.primaryColor);
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors.primaryColor);
    }
    if (colors.secondaryColor) root.style.setProperty('--secondary', colors.secondaryColor);
    if (colors.accentColor) root.style.setProperty('--accent', colors.accentColor);
  }, []);

  const refreshBranding = useCallback(async () => {
    try {
      const b = await api<Branding>('/branding');
      setBranding(b);
      applyTheme(user, b);
      return b;
    } catch {
      return null;
    }
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const [userRes, brandRes] = await Promise.allSettled([
        api<{ user: User; token?: string }>('/auth/me'),
        api<Branding>('/branding'),
      ]);
      const nextUser = userRes.status === 'fulfilled' ? userRes.value.user : null;
      if (userRes.status === 'fulfilled' && userRes.value.token) {
        localStorage.setItem('pb_token', userRes.value.token);
      }
      const nextBrand = brandRes.status === 'fulfilled' ? brandRes.value : branding;
      setUser(nextUser);
      if (brandRes.status === 'fulfilled') setBranding(brandRes.value);
      applyTheme(nextUser, nextBrand);
    } catch {
      localStorage.removeItem('pb_token');
      setUser(null);
      applyTheme(null, branding);
    } finally {
      setLoading(false);
    }
  }, [branding]);

  useEffect(() => {
    void (async () => {
      let b: Branding | null = null;
      try {
        b = await api<Branding>('/branding');
        setBranding(b);
        applyTheme(null, b);
      } catch { /* branding is optional */ }

      try {
        const response = await api<{ user: User; token?: string }>('/auth/me');
        if (response.token) {
          localStorage.setItem('pb_token', response.token);
        }
        setUser(response.user);
        applyTheme(response.user, b);
      } catch {
        localStorage.removeItem('pb_token');
        setUser(null);
        applyTheme(null, b);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await api<{ user: User; token?: string }>('/auth/login', { method: 'POST', ...json({ username, password }) });
    if (response.token) {
      localStorage.setItem('pb_token', response.token);
    }
    setUser(response.user);
    applyTheme(response.user, branding);
  }, [branding]);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch { /* ignore network error */ }
    finally {
      localStorage.removeItem('pb_token');
      setUser(null);
      applyTheme(null, branding);
    }
  }, [branding]);

  const value = useMemo(() => ({ user, loading, branding, login, logout, refresh, refreshBranding, setLiveTheme }), [user, loading, branding, login, logout, refresh, refreshBranding, setLiveTheme]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider.');
  return value;
}
